import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import dayjs from "dayjs";

import { OrdenServicio } from "../../types";

type Cambio = { campo: string; antes: any; despues: any };
type Entrada = { usuario?: string; fecha?: string; cambios?: Cambio[] };

type Props = {
  open: boolean;
  onClose: () => void;
  orden: OrdenServicio | null;
};

// Etiquetas legibles por campo.
const LABELS: Record<string, string> = {
  direccion: "Dirección",
  coordenada: "Coordenadas",
  parroquia: "Parroquia",
  zona: "Zona",
  sector: "Sector",
  pais: "País",
  provincia: "Provincia",
  ciudad: "Ciudad",
  flota: "Flota / técnico",
  fecha_traslado: "Fecha del traslado",
  observaciones: "Observaciones",
  valor_traslado: "Valor del traslado",
  recaudacion_tecnico: "Recaudación técnico",
  cargo_a_factura: "Cargo a factura",
  meses_pago: "Meses a diferir",
  puntos_antes_canje: "Puntos antes de canje",
  puntos_canjeados: "Puntos canjeados",
  id_contrato_softnet: "Contrato Softnet",
};

function parseHistorial(raw: any): Entrada[] {
  if (!raw) return [];
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function fmtVal(v: any): string {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true || v === "true") return "Sí";
  if (v === false || v === "false") return "No";
  return String(v);
}

// Iniciales y color estable/moderado por usuario (mismo usuario => mismo color).
function iniciales(nombre?: string): string {
  const p = (nombre || "?").trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}
function colorUsuario(nombre?: string): string {
  const s = nombre || "?";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h}, 42%, 48%)`;
}

// Corta cualquier texto (incluso cadenas larguísimas sin espacios).
const WRAP = { wordBreak: "break-word" as const, overflowWrap: "anywhere" as const };

/**
 * HISTORIAL DE CAMBIOS (auditoría) de un traslado, en timeline por usuario y
 * tabla campo/antes/después por edición. Cada edición conserva SU usuario/fecha.
 * Datos en traslado.historial_cambios (microservicio).
 */
export default function ModalAuditoriaTraslado({ open, onClose, orden }: Props) {
  const todas = parseHistorial((orden?.traslado as any)?.historial_cambios);
  const total = todas.length;
  const entradas = todas.slice().reverse(); // más reciente primero
  const usuariosDistintos = new Set(todas.map((e) => e.usuario || "—")).size;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, fontWeight: 800, pb: 1.5 }}>
        <HistoryOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.25, ...WRAP }}>
            Auditoría del traslado
            {orden?.numero_orden_servicio ? ` — ${orden.numero_orden_servicio}` : ""}
          </Typography>
          {total > 0 && (
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
              {total} edición(es) · {usuariosDistintos} usuario(s)
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "grey.50" }}>
        {entradas.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
            <HistoryOutlinedIcon sx={{ fontSize: 40, opacity: 0.35 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Sin cambios registrados. Este traslado no ha sido editado por supervisión.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            {entradas.map((e, idx) => {
              const nEdicion = total - idx; // #1 = la más antigua
              const color = colorUsuario(e.usuario);
              const esUltima = idx === entradas.length - 1;
              return (
                <Box key={idx} sx={{ display: "flex", gap: 1.75 }}>
                  {/* Rail del timeline: avatar + línea conectora */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <Tooltip title={`Realizado por ${e.usuario || "usuario desconocido"}`}>
                      <Avatar
                        sx={{
                          bgcolor: color,
                          width: 34,
                          height: 34,
                          fontSize: 13,
                          fontWeight: 700,
                          boxShadow: "0 0 0 3px #fff",
                        }}
                      >
                        {iniciales(e.usuario)}
                      </Avatar>
                    </Tooltip>
                    {!esUltima && (
                      <Box sx={{ flexGrow: 1, width: "2px", bgcolor: "divider", my: 0.5 }} />
                    )}
                  </Box>

                  {/* Contenido de la edición */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      mb: esUltima ? 0 : 2,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    {/* Cabecera de la edición */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ px: 1.75, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, ...WRAP }}>
                        {e.usuario || "Usuario desconocido"}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: "text.secondary" }}>
                        <ScheduleIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption">
                          {e.fecha ? dayjs(e.fecha).format("DD/MM/YYYY HH:mm") : "—"}
                        </Typography>
                      </Stack>
                      <Box flexGrow={1} />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Edición #${nEdicion}`}
                        sx={{ height: 22, fontSize: 11, color: "text.secondary" }}
                      />
                    </Stack>

                    {/* Tabla de cambios: Campo | Antes | Después */}
                    <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                      <TableHead>
                        <TableRow sx={{ "& th": { bgcolor: "grey.100", fontWeight: 700, py: 0.75 } }}>
                          <TableCell sx={{ width: "26%" }}>Campo</TableCell>
                          <TableCell sx={{ width: "37%" }}>Antes</TableCell>
                          <TableCell sx={{ width: "37%" }}>Después</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(e.cambios || []).map((c, i) => (
                          <TableRow key={i} sx={{ verticalAlign: "top" }}>
                            <TableCell sx={{ fontWeight: 600, ...WRAP }}>
                              {LABELS[c.campo] || c.campo}
                            </TableCell>
                            <TableCell sx={{ color: "text.disabled", textDecoration: "line-through", ...WRAP }}>
                              {fmtVal(c.antes)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "success.main", ...WRAP }}>
                              {fmtVal(c.despues)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
